import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, X } from "lucide-react";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ cancelled = false, provider = "stripe" }) {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const paymentRef = provider === "mollie" ? params.get("reference") : params.get("session_id");
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled || !paymentRef) {
      if (!cancelled) setStatus("failed");
      return undefined;
    }
    let attempts = 0;
    const check = async () => {
      attempts += 1;
      try {
        const path = provider === "mollie" ? `payments/mollie/status/${paymentRef}` : `payments/status/${paymentRef}`;
        const response = await axios.get(`${API}/${path}`, { withCredentials: true });
        if (response.data?.payment_status === "paid") setStatus("paid");
        else if (["failed", "expired", "refunded"].includes(response.data?.payment_status)) setStatus("failed");
        else if (attempts >= 30) setStatus("pending");
      } catch {
        if (attempts >= 5) setStatus("failed");
      }
    };
    check();
    const timer = window.setInterval(() => {
      if (attempts >= 30) window.clearInterval(timer);
      else check();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [cancelled, paymentRef, provider]);

  const content = t.payment[status];
  const Icon = status === "paid" ? Check : status === "checking" ? Loader2 : X;
  return (
    <div className="container-x flex min-h-[75vh] items-center justify-center pb-24 pt-36" data-testid="payment-result-page">
      <section className="w-full max-w-2xl border border-white/15 bg-[#0B1530] p-8 text-center sm:p-12" data-testid={`payment-status-${status}`}>
        <Icon className={`mx-auto h-12 w-12 text-[var(--gold)] ${status === "checking" ? "animate-spin" : ""}`} aria-hidden="true" />
        <h1 className="font-display mt-6 text-4xl font-black uppercase sm:text-5xl" data-testid="payment-result-title">{content.title}</h1>
        <p className="mx-auto mt-4 max-w-lg leading-relaxed text-[var(--steel)]" data-testid="payment-result-message">{content.text}</p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/commander" data-testid="payment-back-order" className="bg-[var(--gold)] px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-[var(--night)]">{t.payment.backOrder}</Link>
          <Link to="/" data-testid="payment-back-home" className="border border-white/25 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white">{t.common.backHome}</Link>
        </div>
      </section>
    </div>
  );
}